import React, { useCallback } from "react";
import { useField, useFormikContext } from "formik";
import { useDropzone, FileRejection } from "react-dropzone";
import styled from "styled-components";
import { InputProps } from "react-html-props";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import FieldLabel from "./FieldLabel";
import FieldErrorMessage from "./FieldErrorMessage";
import TextLink from "../TextLink";
import { fileToBase64 } from "../../../helpers";
import FieldWrapper from "./FieldWrapper";

const formatAcceptText = (original: string) =>
  original
    .split(",")
    .map((val) => val.trim())
    .map((val) => val.replace(".", ""))
    .join(", ");

const FileUploadField = (props: FileUploadFieldProps) => {
  const {
    accept,
    acceptHint,
    label,
    id,
    onChange,
    hiddenLabel,
    children,
    ...otherProps
  } = props;
  const [field, meta] = useField<File | null>(props);
  const { setFieldValue, setFieldError, setFieldTouched } = useFormikContext();

  const setFiles = useCallback(async (files: File[] | FileList) => {
    setFieldTouched(props.name);
    const file = files[0];
    const data = await fileToBase64(file);
    setFieldValue(props.name, {
      filename: file.name,
      data,
    });
    onChange?.();
  }, []);

  const setError = useCallback((rejectedFiles: FileRejection[]) => {
    const { errors } = rejectedFiles[0];
    setFieldTouched(props.name);
    setFieldValue(props.name, null);
    setFieldError(props.name, errors[0].message);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
  } = useDropzone({
    accept,
    onDropAccepted: setFiles,
    onDropRejected: setError,
    maxFiles: 1,

    // Disable the click and keyboard events as we have a button in the UI to
    // trigger the dialog. We use a button over the default behaviour as it's
    // a clearer to let users know the functionality is there.
    noClick: true,
    noKeyboard: true,
  });

  const showError = Boolean(meta.touched && meta.error);
  const htmlFor = id || otherProps.name;

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={htmlFor} visuallyHidden={hiddenLabel}>
        {label}
      </FieldLabel>
      <FileUpload
        {...getRootProps()}
        active={isDragActive}
        success={Boolean(field.value)}
        error={showError}
      >
        <input
          {...getInputProps()}
          {...field}
          {...otherProps}
          id={htmlFor}
          // Need to pass value as undefined so Formik doesn't try to control the
          // input value. Otherwise we get an error when it tries to set the value
          // on an input with type 'file'.
          value={undefined}
          onChange={(e) => setFiles(e.target.files)}
        />
        <InstructionText>
          Drag and drop a file, or{" "}
          <TextLink as="button" type="button" onClick={openFileDialog}>
            browse
          </TextLink>
        </InstructionText>
        {accept && (
          <SupportedFilesMessage>
            {acceptHint ? acceptHint : `Supports: ${formatAcceptText(accept)}`}
          </SupportedFilesMessage>
        )}
        {field.value && (
          <SelectedFile>
            File selected <SelectedFileIcon icon={faCheck} />
          </SelectedFile>
        )}
      </FileUpload>
      <FieldErrorMessage name={otherProps.name} />
      {children}
    </FieldWrapper>
  );
};

export interface FileUploadFieldProps extends InputProps {
  label: string;
  name: string;
  acceptHint?: string;
  children?: React.ReactNode;
  hiddenLabel?: boolean;
  onChange?: () => void;
}

const FileUpload = styled.div<{
  active: boolean;
  error: boolean;
  success: boolean;
}>`
  padding: 1.5rem;
  width: 100%;
  text-align: center;
  border-style: dashed;
  border-width: 2px;
  border-color: ${({ theme }) => theme.colors.border};
  border-radius: 5px;
  box-sizing: border-box;
  transition: border 0.24s ease-in-out, background-color 0.24s ease-in-out;

  :focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.primaryLight};
  }

  ${(props) => {
    const { active, success, error, theme } = props;

    if (active) {
      return `
        border-color: ${theme.colors.primary};
        background-color: ${theme.colors.primaryLight};
    `;
    }
    if (success) {
      return `
        border-color: ${theme.colors.success};
        background-color: ${theme.colors.successLight};
    `;
    }
    if (error) {
      return `
        border-color: ${theme.colors.error};
        background-color: ${theme.colors.errorLight};
    `;
    }

    return null;
  }}
`;

const InstructionText = styled.div`
  margin-bottom: 0.75rem;
`;

const SupportedFilesMessage = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.muted};
`;

const SelectedFile = styled.div`
  margin-top: 0.75rem;
  font-size: 14px;
`;

const SelectedFileIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.success};
`;

export default FileUploadField;
